import { useQuery } from '@tanstack/react-query'
import { EyeIcon } from 'lucide-react'
import { useMemo } from 'react'
import { useParams } from 'react-router'

import { Alert, AlertDescription, AlertTitle } from '@/components/elements/alert'
import { Skeleton } from '@/components/elements/skeleton'
import { Layout } from '@/components/Layout'
import { getDashboard } from '@/services/dashboard.service'
import { getStudentEntityCertificates } from '@/services/entity_student_certificates.service'
import { addCanStartToCourseRecords } from '@/utils/certificates'

import CertificationsSection from './components/CertificationsSection'
import CurrentClassesSection from './components/CurrentClassesSection'
import UpcomingEventsSection from './components/UpcomingEventsSection'
import WelcomeSection from './components/WelcomeSection'

function Dashboard() {
  const { entityId } = useParams()

  const dashboardQuery = useQuery({
    queryKey: ['dashboard', entityId],
    queryFn: () => getDashboard(entityId),
    enabled: !!entityId,
  })

  const studentId = dashboardQuery.data?.studentId

  // Fetch current classes using the same service as My Courses
  const certificateQuery = useQuery({
    queryKey: ['accounts', 'students', 'certificates', entityId, studentId],
    queryFn: () => getStudentEntityCertificates({ entityId, studentId }),
    enabled: !!studentId,
  })

  // Filter to only in-progress certificates and add canStart logic
  const currentClasses = useMemo(() => {
    if (!certificateQuery.data) return []

    return certificateQuery.data.results
      .filter((cert) => cert.status === 'Not Started' || cert.status === 'In Progress')
      .map(addCanStartToCourseRecords)
  }, [certificateQuery.data])

  if (dashboardQuery.isPending) {
    return (
      <Layout breadcrumbs={['Dashboard']}>
        <Layout.Heading title="Dashboard" />
        <div className="space-y-5">
          <Skeleton className="h-48 w-full" />
          <Skeleton className="h-48 w-full" />
          <div className="grid gap-6 md:grid-cols-2">
            <Skeleton className="h-64" />
            <Skeleton className="h-64" />
          </div>
        </div>
      </Layout>
    )
  }

  if (dashboardQuery.isError) {
    return (
      <Layout breadcrumbs={['Dashboard']}>
        <Layout.Heading title="Dashboard" />
        <p className="text-destructive">Error loading dashboard: {dashboardQuery.error.message}</p>
      </Layout>
    )
  }

  const { welcome, recentCertifications, expiringCertifications, isAdminView } =
    dashboardQuery.data

  if (isAdminView) {
    return (
      <Layout breadcrumbs={['Dashboard']}>
        <Layout.Heading title="Dashboard" className="mb-6" />
        <Alert>
          <EyeIcon className="h-4 w-4" />
          <AlertTitle>Admin View</AlertTitle>
          <AlertDescription>
            You are viewing this account as an Xverse administrator. The dashboard displays
            personalized content for enrolled members.
          </AlertDescription>
        </Alert>
        <div className="mt-6">
          <UpcomingEventsSection />
        </div>
      </Layout>
    )
  }

  return (
    <Layout breadcrumbs={['Dashboard']}>
      <Layout.Heading title="Dashboard" className="mb-6" />

      <div className="space-y-5">
        <WelcomeSection welcome={welcome} />

        <CurrentClassesSection
          currentClasses={currentClasses}
          isLoading={certificateQuery.isPending && !!studentId}
        />

        <UpcomingEventsSection />

        <CertificationsSection
          recentCertifications={recentCertifications}
          expiringCertifications={expiringCertifications}
        />
      </div>
    </Layout>
  )
}

export default Dashboard
